import { FilterItem } from './filter-item';
import { BehaviorSubject, ReplaySubject } from 'rxjs';
import { FilterCollection } from './filter-collection';
import { FilterGroupType } from '../enums/filter-group-type';
import { FilterType } from '../enums/filter-type';
import { FilterDisplayHintType } from '../enums/filter-display-hint-type';
import { FilterItemType } from '../enums/filter-item-type';

type ItemNameFunc = (itemType: FilterItemType | string) => string;

export class FilterGroup {
  readonly Collection: FilterCollection;
  readonly GroupType: FilterGroupType;
  readonly FilterType: FilterType;
  DisplayHintType: FilterDisplayHintType;
  IsAlwaysVisible: boolean;

  private items: FilterItem[];
  get Items(): FilterItem[] {
    return this.items;
  }
  private readonly itemsSubject = new ReplaySubject<FilterItem[]>(1);
  readonly Items$ = this.itemsSubject.asObservable();

  private isVisibleSubject = new BehaviorSubject<boolean>(true);
  readonly IsVisible$ = this.isVisibleSubject.asObservable();

  private isActive = false;
  private isActiveSubject = new BehaviorSubject<boolean>(false);
  readonly IsActive$ = this.isActiveSubject.asObservable();
  get IsActive(): boolean {
    return this.isActive;
  }


  private readonly itemNameFunc: ItemNameFunc;
  public GetItemName(type: FilterItemType | string): string {
    if (!this.itemNameFunc) return type;

    return this.itemNameFunc(type);
  }

  constructor(collection: FilterCollection, groupType: FilterGroupType, filterType: FilterType);
  constructor(collection: FilterCollection, groupType: FilterGroupType, filterType: FilterType, itemTypesOrNameFunc: FilterItemType[] | ItemNameFunc);
  constructor(collection: FilterCollection, groupType: FilterGroupType, filterType: FilterType, itemTypesOrNameFunc: FilterItemType[] | ItemNameFunc);
  constructor(collection: FilterCollection, groupType: FilterGroupType, filterType: FilterType, itemTypesOrNameFunc: FilterItemType[] | ItemNameFunc, displayHintType: FilterDisplayHintType);
  constructor(
    collection: FilterCollection,
    groupType: FilterGroupType,
    filterType: FilterType,
    itemTypesOrNameFunc: FilterItemType[] | ItemNameFunc,
    displayHintType: FilterDisplayHintType,
    isAlwaysVisible: boolean
  );
  constructor(
    collection: FilterCollection,
    groupType: FilterGroupType,
    filterType: FilterType,
    itemTypesOrNameFunc: FilterItemType[] | ItemNameFunc = [],
    displayHintType: FilterDisplayHintType = FilterDisplayHintType.None,
    isAlwaysVisible: boolean = true
  ) {
    this.Collection = collection;
    this.GroupType = groupType;
    this.FilterType = filterType;
    if (Array.isArray(itemTypesOrNameFunc))
      this.items = itemTypesOrNameFunc.map(itemType => new FilterItem(this, itemType, itemType.replace(' ', '')));
    else
      this.itemNameFunc = itemTypesOrNameFunc;
    this.itemsSubject.next(this.items);
    this.DisplayHintType = displayHintType;
    this.IsAlwaysVisible = isAlwaysVisible;
    if (this.IsAlwaysVisible) {
      this.setIsVisible(true);
    }
  }

  setItems(items: FilterItem[]): void {
    this.items = items;
    this.itemsSubject.next(this.items);
  }

  setDisplayHintType(displayHintType: FilterDisplayHintType): void {
    this.DisplayHintType = displayHintType;
  }

  setIsVisible(isVisible: boolean): void {
    if (this.IsAlwaysVisible) {
      this.isVisibleSubject.next(true);
    } else {
      this.isVisibleSubject.next(isVisible);
    }
  }

  setIsAlwaysVisible(isAlwaysVisible: boolean): void {
    this.IsAlwaysVisible = isAlwaysVisible;
    if (this.IsAlwaysVisible) {
      this.setIsVisible(true);
    }
  }

  setIsActive(isActive: boolean): void {
    this.isActive = isActive;
    this.isActiveSubject.next(this.isActive);
  }

  reset(): void {
    this.setIsActive(false);
    this.items.forEach(i => i.reset());
  }

  raiseFiltersChanged(...items: FilterItem[]): void {
    this.Collection.raiseFiltersChanged(...items);
  }
}
